// Counter Intelligence — in-chat price update helpers.
//
// Counter Intelligence can update item prices from an uploaded invoice or price
// list right inside the chat. It runs the file through the SAME price-update
// processor the Catalog & Prices → Update Prices page uses — the price-update-ai
// extractor, the shared candidate scoring, and the deterministic dedup classifier
// (importDedup.classifyItem) — then shows the proposed price changes back to the
// user for approval before anything is written.
//
// The flow mirrors the Update Prices page, but stays in the chat:
//   1. offer       — "Want me to update your prices from an invoice?" (yes/no)
//   2. upload       — the user picks one or more files
//   3. processing   — extract line items, match each against the catalog
//   4. review       — proposed price changes (old → new), new items, and
//                     unchanged items, grouped; remove any, then approve
//   5. apply        — update changed prices, add new items, skip unchanged,
//                     recording every change to price history
//   6. summary      — recap ("updated 8 prices, added 2 items, 5 unchanged")
//
// Matching an imported item to an existing one is a PRICE UPDATE when the price
// differs — that is the whole point of this flow. Items with the same price are
// left untouched; items with no catalog match are offered as new.
//
// Pure (no React / no I/O) so it is unit-testable. The drawer
// (CounterIntelligenceDrawer.jsx) owns the I/O — reading files, the edge calls,
// the review card, and the DB writes.

import { classifyItem } from './importDedup';

const PRICE_TTL_MS = 30 * 60 * 1000; // 30 minutes, same as the other CI pendings.

// The file types the chat price update accepts — kept in lockstep with the
// Update Prices page upload control (UpdatePrices.jsx ACCEPTED_TYPES).
export const PRICE_UPDATE_ACCEPT =
  '.jpg,.jpeg,.png,.webp,.heic,.pdf,.csv,.xlsx,.xls';

// One plain-English sentence for "what files can you take for a price update?"
export const PRICE_UPDATE_FILES_SENTENCE =
  'Upload a vendor invoice or a price list — a PDF, an Excel/CSV spreadsheet, or a photo (PNG, JPG, HEIC). I read the line items, match them to your catalog, and show you which prices changed before saving.';

// The short nudge the drawer shows when it offers the in-chat price update. The
// support answer already explained the workflow; this is just the actionable offer.
export const PRICE_UPDATE_OFFER_TEXT =
  'Want to update your prices right here? Upload a vendor invoice or price list and I’ll match the items to your catalog, show you exactly which prices changed (and any new items), then update them once you approve.';

export function priceUpdateOfferChips() {
  return [
    { key: 'price_yes', label: 'Yes, upload an invoice' },
    { key: 'price_no', label: 'Not now' },
  ];
}

// ── Step 1: the offer ────────────────────────────────────────────────────────

const nowIso = () => new Date().toISOString();
const plusMs = (ms) => new Date(Date.now() + ms).toISOString();

export function buildPriceUpdateOfferPending({ text } = {}) {
  return {
    kind: 'pending_price_update',
    awaiting: 'offer',
    original_user_message: String(text || ''),
    created_at: nowIso(),
    expires_at: plusMs(PRICE_TTL_MS),
  };
}

// ── Step 4: build the review rows from matched invoice items ──────────────────
// Mirrors UpdatePrices.handleProcess: classification comes from the deterministic
// dedup (importDedup.classifyItem), not from the AI match — the AI match only
// enriches each row with what it thinks the catalog entry is. Every row carries
// the fields the apply step needs (_classification, _existingItem, the deltas).
//
// `matched` is [{ invoiceItem, catalogMatch, confidence, reason }] from the
// extract+match passes. `catalogLookup` is importDedup.buildCatalogLookup(catalog).
export function buildPriceUpdateRows({ matched, catalogLookup }) {
  const rows = [];
  let tempIdx = 0;
  for (const m of (matched || [])) {
    const invoiceItem = m.invoiceItem || {};
    const synthetic = {
      description: invoiceItem.name || '',
      vendor: invoiceItem.vendor || '',
      unit: invoiceItem.unit || '',
      case_price: invoiceItem.price,
    };
    const dedup = classifyItem(synthetic, catalogLookup);
    const classification = dedup.classification; // 'unchanged' | 'price_change' | 'new'
    const existing = dedup.existingItem;

    const id = (classification !== 'new' && existing?.id)
      ? String(existing.id)
      : `_temp_${tempIdx++}`;

    const oldPrice = existing?.case_price != null ? parseFloat(existing.case_price) : null;
    const newPrice = invoiceItem.price != null ? parseFloat(invoiceItem.price) : null;

    rows.push({
      id,
      description: invoiceItem.name || existing?.description || 'Unknown Item',
      vendor: invoiceItem.vendor || existing?.vendor || null,
      unit: invoiceItem.unit || existing?.unit || null,
      category: existing?.category || null,
      location: existing?.location || null,
      case_price: newPrice ?? oldPrice ?? 0,
      oldPrice,
      newPrice,
      confidence: m.confidence || (dedup.fuzzyMatched ? 'fuzzy' : 'none'),
      fuzzyMatched: !!dedup.fuzzyMatched,
      _classification: classification,
      _existingItem: existing,
      _priceDeltaAbsolute: dedup.priceDeltaAbsolute,
      _priceDeltaPct: dedup.priceDeltaPct,
      _invoiceItem: invoiceItem,
      _invoiceVendor: invoiceItem._invoiceVendor ?? null,
      _invoiceDate: invoiceItem._invoiceDate ?? null,
      _invoiceNumber: invoiceItem._invoiceNumber ?? null,
    });
  }
  return rows;
}

// Count rows by classification so the review card and the offer can say
// "8 price changes · 2 new · 5 unchanged".
export function summarizeRowCounts(rows) {
  const counts = { priceChanges: 0, newItems: 0, unchanged: 0 };
  (rows || []).forEach((r) => {
    if (r._classification === 'price_change') counts.priceChanges += 1;
    else if (r._classification === 'new') counts.newItems += 1;
    else counts.unchanged += 1;
  });
  return counts;
}

// The rows that actually get written when the user approves — price changes and
// new items. Unchanged rows are shown for transparency but never written.
export function actionableRows(rows) {
  return (rows || []).filter((r) => r._classification === 'price_change' || r._classification === 'new');
}

// ── Step 6: the recap ────────────────────────────────────────────────────────
// "I updated 8 prices, added 2 new items, and left 5 unchanged." Reads from the
// applied result so it reflects exactly what was written.
const fmtMoney = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
};

export function summarizePriceUpdate(result) {
  const r = result || {};
  const updated = Number(r.updated) || 0;
  const added = Number(r.added) || 0;
  const skipped = Number(r.skipped) || 0;
  const parts = [];
  if (updated) parts.push(`updated ${updated} price${updated === 1 ? '' : 's'}`);
  if (added) parts.push(`added ${added} new item${added === 1 ? '' : 's'}`);
  if (!parts.length) {
    return skipped
      ? `Nothing to change — all ${skipped} item${skipped === 1 ? '' : 's'} already matched the prices in your catalog.`
      : 'No price changes were applied.';
  }
  let sentence = `I ${joinParts(parts)}`;
  if (skipped) sentence += `, and left ${skipped} unchanged`;
  sentence += '. These prices apply to future counts and orders — completed counts and past reports stay as they were.';
  return sentence;
}

function joinParts(parts) {
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

// A one-line description of a single price-change row for the review card, e.g.
// "Tito's Vodka 1.75L  $24.00 → $26.50 (+10.4%)".
export function describePriceChange(row) {
  const r = row || {};
  const delta = r._priceDeltaPct;
  const pct = (delta != null && Number.isFinite(delta))
    ? ` (${delta > 0 ? '+' : ''}${delta.toFixed(1)}%)`
    : '';
  return `${fmtMoney(r.oldPrice)} → ${fmtMoney(r.newPrice ?? r.case_price)}${pct}`;
}
